import { cn } from '@/lib/cn';
import { Fade } from '@heidi/design-system/fade';
import { useVerticalScrollFade } from '@heidi/design-system/hooks/use-vertical-scroll-fade';
import { useRef } from 'react';

/**
 * Each word is its own element so a newly streamed token mounts and fades once.
 * Mutating a paragraph's text in place fires no animation, which is why the
 * block-level `block-settle` rule alone leaves mid-paragraph streaming static.
 */
const WORD_FADE_CLASS =
  'motion-safe:animate-in motion-safe:fade-in motion-safe:fill-mode-both motion-safe:duration-500';

const StreamedLine = ({ text, isStreaming }: { text: string; isStreaming: boolean }) => {
  if (!text) return <>{'\u00A0'}</>;
  if (!isStreaming) return <>{text}</>;

  return (
    <>
      {text.split(/(\s+)/).map((token, index) =>
        token.trim() === '' ? (
          token
        ) : (
          <span key={index} className={WORD_FADE_CLASS}>
            {token}
          </span>
        )
      )}
    </>
  );
};

/**
 * Expandable scroll region for inline edit responses. Mounted for the whole
 * generating/review phase — including before the first chunk — so the
 * `0fr -> 1fr` row transition has a painted starting value to animate from.
 */
export const InlineEditStreamingBody = ({
  text,
  isStreaming,
}: {
  text: string;
  isStreaming: boolean;
}) => {
  const trimmed = text.trim();
  const hasText = trimmed.length > 0;
  const lines = text.split('\n');
  const useBulletList = lines.some((line) => line.trimStart().startsWith('- '));
  const bulletLines = lines.filter((line) => line.trim());
  const scrollRef = useRef<HTMLDivElement>(null);
  const { canScrollUp, canScrollDown } = useVerticalScrollFade(scrollRef, hasText, [
    text,
    isStreaming,
  ]);

  return (
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-300 ease-out-quart motion-reduce:transition-none',
        hasText ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        // Cancels the bar's `gap-2` while collapsed so the empty shell adds no
        // dead space above the loader row.
        !hasText && '-mb-2'
      )}
      data-testid="inline-edit-streaming-shell"
    >
      <div className="min-h-0 overflow-hidden">
        <div className="relative [--fade-bg:hsl(var(--twc-surface))]">
          <div
            ref={scrollRef}
            data-testid="inline-edit-streaming-body"
            className="scrollbar-subtle max-h-48 overflow-y-auto rounded-md text-sm text-text-primary"
            data-streaming={isStreaming ? 'true' : undefined}
          >
            {useBulletList ? (
              <ul className="list-disc ps-4">
                {bulletLines.map((line, index) => (
                  <li key={index}>
                    <StreamedLine
                      text={line.trimStart().replace(/^-\s*/, '')}
                      isStreaming={isStreaming}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              lines.map((line, index) => (
                <p key={index} className={line === '' ? 'min-h-[1em]' : undefined}>
                  <StreamedLine text={line} isStreaming={isStreaming} />
                </p>
              ))
            )}
          </div>
          {canScrollUp ? (
            <Fade
              side="top"
              background="var(--fade-bg)"
              className="inset-x-0 top-0 h-6"
              data-testid="inline-edit-scroll-top-fade"
            />
          ) : null}
          {canScrollDown ? (
            <Fade
              side="bottom"
              background="var(--fade-bg)"
              className="inset-x-0 bottom-0 h-6"
              data-testid="inline-edit-scroll-bottom-fade"
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
